import React, { createContext, useContext, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { T as Tds } from '../design-system/tokens';

const ToastCtx = createContext(null);
export const useToast = () => useContext(ToastCtx);

const STYLES = {
  success: { bg: '#ECFDF5', border: '#6EE7B7', accent: '#059669', text: '#065F46' },
  error:   { bg: '#FEF2F2', border: '#FECACA', accent: '#DC2626', text: '#991B1B' },
  warning: { bg: '#FFFBEB', border: '#FDE68A', accent: '#D97706', text: '#92400E' },
  info:    { bg: '#EFF6FF', border: '#BFDBFE', accent: '#3B82F6', text: '#1E40AF' },
};

const ICheck = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);
const IError = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);
const IWarn = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const IInfo = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>
);
const IX = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const ICONS = { success: <ICheck />, error: <IError />, warning: <IWarn />, info: <IInfo /> };

function ToastItem({ id, message, type, onDismiss }) {
  const s = STYLES[type] || STYLES.info;
  return (
    <div
      role="alert"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '12px 14px 12px 12px',
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderLeft: `3px solid ${s.accent}`,
        borderRadius: 10,
        boxShadow: '0 4px 20px rgba(0,0,0,0.09), 0 1px 4px rgba(0,0,0,0.05)',
        fontFamily: "'Plus Jakarta Sans','Inter',system-ui,sans-serif",
        fontSize: Tds.type.sm,
        color: s.text,
        minWidth: 260,
        maxWidth: 380,
        animation: 'aone-toast-in 0.22s cubic-bezier(0.22,0.61,0.36,1) both',
        pointerEvents: 'auto',
        lineHeight: 1.5,
      }}
    >
      <span style={{ color: s.accent, flexShrink: 0, marginTop: 1 }}>{ICONS[type]}</span>
      <span style={{ flex: 1, fontWeight: 500 }}>{message}</span>
      <button
        onClick={() => onDismiss(id)}
        aria-label="Dismiss"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: s.text, opacity: 0.45, padding: 2, flexShrink: 0,
          marginTop: 1, lineHeight: 1, display: 'flex', alignItems: 'center',
          transition: 'opacity 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '0.45'; }}
      >
        <IX />
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);

  const add = useCallback((message, type = 'info', duration = 3500) => {
    const id = Date.now() + Math.random();
    // Cap at 5 visible toasts to avoid stacking chaos
    setToasts(prev => [...prev.slice(-4), { id, message, type }]);
    if (duration > 0) setTimeout(() => dismiss(id), duration);
  }, [dismiss]);

  const ctx = {
    toast:   (msg, dur) => add(msg, 'info', dur),
    success: (msg, dur) => add(msg, 'success', dur),
    error:   (msg, dur) => add(msg, 'error', dur),
    warning: (msg, dur) => add(msg, 'warning', dur),
    info:    (msg, dur) => add(msg, 'info', dur),
  };

  return (
    <ToastCtx.Provider value={ctx}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <div
          role="region"
          aria-label="Notifications"
          aria-live="polite"
          style={{
            position: 'fixed', top: 20, right: 20, zIndex: 9999,
            display: 'flex', flexDirection: 'column', gap: 8,
            pointerEvents: 'none',
          }}
        >
          {toasts.map(t => <ToastItem key={t.id} {...t} onDismiss={dismiss} />)}
        </div>,
        document.body
      )}
    </ToastCtx.Provider>
  );
}
